import React from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, Grid } from '@mui/material';
import { Icon } from '@iconify/react';
import { useIdleTimer } from 'react-idle-timer';
import { useNavigate } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';

import { environment } from 'src/environment/environment';
import { signOutUser } from 'src/services/service-handlers/PrivateService';
import { getErrorMessage } from 'src/utils/ErrorUtils';
import AuthUtils from 'src/utils/AuthUtils';
import { useAuthContext } from 'src/context/AuthContext';
import FormConstants from 'src/constants/FormConstants';
import EncryptUtils from 'src/utils/EncryptUtils';
import { toast } from 'src/components/snackbar';

const AutoLogout = (props: any): React.ReactElement => {
  const { authState, logout } = useAuthContext();
  const navigate = useNavigate();
  const isAzureAuthenticated = useIsAuthenticated();
  const { instance } = useMsal();
  const { isAdmin } = authState;

  // Fetch dynamic timeout from admin configurations based on user type
  const [timeoutMinutes, setTimeoutMinutes] = React.useState<number>(environment.InActivityTimeOutInMins);
  const [promptBeforeIdleMinutes, setPromptBeforeIdleMinutes] = React.useState<number>(environment.InActivityPromptBeforeIdleInMins);

  React.useEffect(() => {
    const fetchTimeoutConfig = async () => {
      try {
        const { getTimeoutConfig } = await import('src/services/service-handlers/PrivateService');
        // Fetch appropriate timeout based on user type (admin or user)
        const response = await getTimeoutConfig();
        if (response?.configValue && typeof response.configValue === 'number') {
          setTimeoutMinutes(response.configValue);
        }
      } catch (error) {
        console.error('Error fetching timeout configuration:', error);
        // Fallback to environment default
      }
    };
    fetchTimeoutConfig();
  }, [isAdmin]);

  // Recalculate timeout values when timeoutMinutes changes
  const timeout = React.useMemo(() => 1000 * 60 * timeoutMinutes, [timeoutMinutes]);
  const promptBeforeIdle = React.useMemo(() => 1000 * 60 * promptBeforeIdleMinutes, [promptBeforeIdleMinutes]);

  const { children } = props;

  const [remaining, setRemaining] = React.useState<number>(timeout);
  const [open, setOpen] = React.useState<boolean>(false);

  const handleLogOut = async () => {
    try {
      const data: any = {
        sql_data: {
          is_audit: true,
          audit_event_description: FormConstants.COMMON.LOGOUT_SUCCESS,
        },
      };
      const encryptRequestData = EncryptUtils.encryptedData(data);
      await signOutUser({
        tk: encryptRequestData,
      });
      if (isAzureAuthenticated || AuthUtils.getIsAzureAuthenticated()) {
        instance.logoutRedirect({
          postLogoutRedirectUri: '/',
        });
      } else {
        logout();
      }
      AuthUtils.clearStorage();
      // Redirect based on admin status
      navigate(isAdmin ? '/admin/sign-in' : '/sign-in', { replace: true });
    } catch (e) {
      const errMessage = getErrorMessage(e);
      toast.error(errMessage);
    }
  };

  const onIdle = () => {
    setOpen(false);
    handleLogOut();
  };

  const onActive = () => {
    setOpen(false);
  };

  const onPrompt = () => {
    setOpen(true);
  };

  const { getRemainingTime, activate, reset } = useIdleTimer({
    onIdle,
    onActive,
    onPrompt,
    timeout,
    promptBeforeIdle,
    throttle: 500,
  });

  // Reset timer when timeout changes to ensure it uses the new timeout value
  React.useEffect(() => {
    // Reset the timer when timeout value changes (e.g., after fetching from admin config)
    // This ensures the timer uses the new timeout duration immediately
    if (reset) {
      reset();
    }
  }, [timeout, promptBeforeIdle, reset]);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setRemaining(Math.ceil(getRemainingTime() / 1000));
    }, 500);

    return () => {
      clearInterval(interval);
    };
  });

  return (
    <>
      {children}

      {open && (
        <Dialog open={open} onClose={handleLogOut}>
          <DialogTitle>{FormConstants.AUTO_LOGOUT.TITLE}</DialogTitle>

          <DialogContent
            sx={{
              padding: '1rem',
              margin: '1.5rem',
              background: 'background.paper',
              borderRadius: '0.25rem',
            }}
          >
            <Grid container spacing={2} my="5px">
              <Grid item xs={1}>
                <Icon icon="mdi:alert" color="red" width="24" height="24" />
              </Grid>

              <Grid item xs={11}>
                <div style={{ fontWeight: '500', fontSize: '1rem' }}>
                  {FormConstants.AUTO_LOGOUT.SESSION_TIMEOUT_MESSAGE}
                </div>
                <div style={{ marginTop: '0.5rem', fontSize: '0.95rem' }}>
                  You will be logged out in{' '}
                  <span style={{ color: '#f44336', fontWeight: 'bold', fontSize: '1.2rem' }}>
                    {remaining}
                  </span>{' '}
                  seconds. Press stay logged in to continue the session.
                </div>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={handleLogOut}
              style={{ backgroundColor: '#f44336', color: '#fff' }}
              variant="outlined"
            >
              {FormConstants.AUTO_LOGOUT.LOG_OUT}
            </Button>
            <Button
              onClick={activate}
              style={{
                backgroundColor: '#4caf50',
                color: '#fff',
              }}
              variant="contained"
            >
              {FormConstants.AUTO_LOGOUT.STAY_LOGGED_IN}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default AutoLogout;
